import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { Helmet } from 'react-helmet';

import { Poetry, PoetryClubInfo, usePoetry } from 'api/poetryClub';
import { Box, CustomBox, GridChild, GridContainer, Title } from 'common';
import { useEffect, useState } from 'react';
import { PoetryCard } from 'widgets/cards';

import { useLanguage } from '../../context/LanguageContext';
import { poetryClubPageLabels } from '../../locales/translations'

// Картинки и таблицы из редактора не должны вылезать за экран на мобильных
export const richTextSx = {
    '& img': { maxWidth: '100%', height: 'auto' },
    '& iframe': { maxWidth: '100%' },
    '& table': { maxWidth: '100%', display: 'block', overflowX: 'auto' }
};

export const PoetryPage = () => {
    const [clubInfo, setClubInfo] = useState<PoetryClubInfo | null>(null);
    const [poetryList, setPoetryList] = useState<Poetry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    const { onGetAllPoetry, onGetPoetryClubInfo } = usePoetry();

    const { language } = useLanguage();
    const title = clubInfo?.[`title_${language}`] || poetryClubPageLabels.title[language];
    const description = clubInfo?.[`description_${language}`] || clubInfo?.description;
    const pageTitle = poetryClubPageLabels.pageTitle[language];
    const metaDescription = poetryClubPageLabels.metaDescription[language];
    const meetingsTitle = poetryClubPageLabels.meetingsTitle[language];
    const noMeetings = poetryClubPageLabels.noMeetings[language];
    const loadError = poetryClubPageLabels.loadError[language];

    useEffect(() => {
        (async () => {
            const [infoResult, listResult] = await Promise.all([
                onGetPoetryClubInfo(),
                onGetAllPoetry()
            ]);

            // Страница клуба может быть ещё не заполнена в админке — тогда показываем только заседания
            if (infoResult.success) {
                setClubInfo(infoResult.data);
            }

            if (listResult.success) {
                const sortedPoetry = listResult.data
                    .filter((a: Poetry) => a.show)
                    .sort((a: Poetry, b: Poetry) =>
                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    );
                setPoetryList(sortedPoetry);
            } else {
                setError(true);
            }

            setLoading(false);
        })();
    }, [onGetAllPoetry, onGetPoetryClubInfo]);

    return (
        <CustomBox title={title}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
            </Helmet>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {clubInfo && (
                        <GridContainer alignItems="center" sx={{ marginBottom: 4 }}>
                            {clubInfo.photo && (
                                <GridChild md={5}>
                                    <img
                                        src={clubInfo.photo}
                                        alt={title}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            maxHeight: 460,
                                            objectFit: 'cover',
                                            borderRadius: 8
                                        }}
                                    />
                                </GridChild>
                            )}
                            <GridChild md={clubInfo.photo ? 7 : 12}>
                                <Title variant="body1" color="text.secondary" component="div" sx={richTextSx}>
                                    <div dangerouslySetInnerHTML={{ __html: description || '' }} />
                                </Title>
                            </GridChild>
                        </GridContainer>
                    )}

                    <CustomBox title={meetingsTitle} sx={{ paddingY: 0 }}>
                        {error ? (
                            <Alert severity="error">{loadError}</Alert>
                        ) : poetryList.length === 0 ? (
                            <Title color="text.secondary">{noMeetings}</Title>
                        ) : (
                            <GridContainer>
                                {poetryList.map((poetryItem) => (
                                    <GridChild xs={12} sm={6} md={4} lg={3} key={poetryItem.id}>
                                        <PoetryCard poetryItem={poetryItem} />
                                    </GridChild>
                                ))}
                            </GridContainer>
                        )}
                    </CustomBox>
                </>
            )}
        </CustomBox>
    );
};
